/**
 * Normalización de restaurants.whatsapp a E.164 (sólo dígitos, con lada país).
 * En producción conviven URLs (wa.me, api.whatsapp.com, web.whatsapp.com)
 * y teléfonos planos. El país NO está hardcodeado: se recibe desde la
 * configuración de pedidos y México es sólo el valor por defecto.
 */

export const DEFAULT_DIAL_CODE = '52';

/** Ladas soportadas en el selector. Ampliable sin tocar la lógica. */
export const COUNTRY_DIAL_OPTIONS: Array<{ code: string; label: string }> = [
  { code: '52', label: 'México (+52)' },
  { code: '1', label: 'EE. UU. / Canadá / República Dominicana (+1)' },
  { code: '34', label: 'España (+34)' },
  { code: '353', label: 'Irlanda (+353)' },
  { code: '44', label: 'Reino Unido (+44)' },
  { code: '33', label: 'Francia (+33)' },
  { code: '49', label: 'Alemania (+49)' },
  { code: '39', label: 'Italia (+39)' },
  { code: '351', label: 'Portugal (+351)' },
  { code: '31', label: 'Países Bajos (+31)' },
  { code: '47', label: 'Noruega (+47)' },
  { code: '57', label: 'Colombia (+57)' },
  { code: '54', label: 'Argentina (+54)' },
  { code: '56', label: 'Chile (+56)' },
  { code: '51', label: 'Perú (+51)' },
  { code: '593', label: 'Ecuador (+593)' },
  { code: '502', label: 'Guatemala (+502)' },
  { code: '503', label: 'El Salvador (+503)' },
  { code: '504', label: 'Honduras (+504)' },
  { code: '505', label: 'Nicaragua (+505)' },
  { code: '506', label: 'Costa Rica (+506)' },
  { code: '507', label: 'Panamá (+507)' },
  { code: '58', label: 'Venezuela (+58)' },
  { code: '591', label: 'Bolivia (+591)' },
  { code: '598', label: 'Uruguay (+598)' },
  { code: '595', label: 'Paraguay (+595)' },
  { code: '55', label: 'Brasil (+55)' },
];


const DIAL_CODES = COUNTRY_DIAL_OPTIONS.map((option) => option.code);

/** Devuelve una lada válida (sólo dígitos) o la de México por defecto. */
export function sanitizeDialCode(raw: unknown): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits || digits.length > 4) return DEFAULT_DIAL_CODE;
  return digits;
}

/**
 * Devuelve el número en formato E.164 sin el "+" (sólo dígitos) o null si no
 * es utilizable. `dialCode` sólo se aplica cuando el número viene en formato
 * nacional (sin lada país).
 */
export function normalizeWhatsappNumber(
  raw: string | null | undefined,
  dialCode: string = DEFAULT_DIAL_CODE
): string | null {
  if (!raw) return null;
  const value = String(raw).trim();
  if (!value) return null;

  const country = sanitizeDialCode(dialCode);
  const explicitInternational = value.trim().startsWith('+');

  let candidate = value;

  // URLs con el número en query string (?phone=...)
  const phoneParam = value.match(/[?&]phone=([+\d\s()-]+)/i);
  if (phoneParam) {
    candidate = phoneParam[1];
  } else {
    // wa.me/<numero> o whatsapp.com/send/<numero>
    const pathMatch = value.match(/(?:wa\.me|whatsapp\.com\/(?:send|message))\/+([+\d\s()-]+)/i);
    if (pathMatch) candidate = pathMatch[1];
  }

  let digits = candidate.replace(/\D/g, '');
  if (!digits) return null;

  // Prefijo internacional mexicano con el 1 antiguo de celular: 52 1 XXXXXXXXXX
  if (digits.length === 13 && digits.startsWith('521')) {
    digits = '52' + digits.slice(3);
  }

  // Número nacional (sin lada país): anteponemos la lada configurada.
  if (!explicitInternational && !startsWithKnownDialCode(digits, country)) {
    digits = country + digits;
  }

  if (digits.length < 8 || digits.length > 15) return null;
  return digits;
}

/** ¿El número ya trae una lada país reconocible (la configurada u otra conocida)? */
function startsWithKnownDialCode(digits: string, country: string): boolean {
  // Un número que ya empieza con la lada configurada y es más largo que un
  // número nacional típico se considera internacional.
  if (digits.startsWith(country) && digits.length > 10) return true;
  return DIAL_CODES.some((code) => code.length >= 2 && digits.startsWith(code) && digits.length > 10);
}

/** Enlace oficial de click-to-chat con el texto precargado y codificado. */
export function buildWhatsappLink(digits: string, text: string): string {
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}
